import { TERRAIN_STYLES, TOURNAMENT_ROUNDS, TURN_LIMITS, WORLDS, type GameSummary } from '@tanky/shared';
import { api } from '../api';
import { formatLimit, formatRemaining, serverNow } from '../clock';
import { h, toast } from '../dom';
import { go, session, type Screen } from '../session';
import { socket } from '../ws';
import { tankEditor } from './tankEditor';
import { leaderboardCard } from './leaderboard';
import { soundButton } from './soundButton';
import { notificationButton } from '../notifications';
import { modeLabel, roundsText } from './standings';

const SHOW_FINISHED_KEY = 'tanky.show_finished';

function statusBadge(g: GameSummary): HTMLElement {
  const me = session.me!.id;
  if (g.status === 'lobby') return h('span', { class: 'badge' }, 'Čekárna');
  if (g.status === 'finished') {
    if (g.winnerId === me) return h('span', { class: 'badge good' }, 'Vyhrál jsi! 🏆');
    const w = g.players.find((p) => p.id === g.winnerId);
    return h('span', { class: 'badge' }, w ? `Vyhrál ${w.name}` : 'Remíza');
  }
  if (g.settings.mode === 'deathmatch') return h('span', { class: 'badge hot' }, '💥 Probíhá');
  if (g.currentPlayerId === me) return h('span', { class: 'badge hot' }, 'Jsi na tahu!');
  const cur = g.players.find((p) => p.id === g.currentPlayerId);
  return h('span', { class: 'badge' }, `Hraje ${cur?.name ?? '…'}`);
}

function rank(g: GameSummary): number {
  if (g.status === 'playing' && g.currentPlayerId === session.me!.id) return 0;
  if (g.status === 'playing') return 1;
  if (g.status === 'lobby') return 2;
  return 3;
}

export function lobbyScreen(): Screen {
  const list = h('div', { class: 'game-list' }, h('p', { class: 'muted' }, 'Načítám…'));
  let games: GameSummary[] = [];

  // Dohrané hry jsou ve sbalitelné sekci; element vzniká jednou, aby se při překreslení nezavíral.
  const finishedSummary = h('summary', {});
  const finishedList = h('div', { class: 'game-list' });
  const finishedBox = h('details', { class: 'finished-games' }, finishedSummary, finishedList);
  try {
    finishedBox.open = localStorage.getItem(SHOW_FINISHED_KEY) === '1';
  } catch {}
  finishedBox.addEventListener('toggle', () => {
    try {
      localStorage.setItem(SHOW_FINISHED_KEY, finishedBox.open ? '1' : '0');
    } catch {}
  });

  function gameItem(g: GameSummary): HTMLElement {
    const right =
      g.status === 'playing' && g.deadline
        ? `⏱ ${formatRemaining(g.deadline - serverNow())}`
        : g.status === 'lobby'
          ? `kód ${g.code}`
          : '';
    return h(
      'button',
      { class: `game-item${rank(g) === 0 ? ' my-turn' : ''}`, onclick: () => go(`/game/${g.id}`) },
      h('span', { class: 'names' }, g.players.map((p) => p.name).join(', ')),
      statusBadge(g),
      h(
        'span',
        { class: 'muted small' },
        [
          modeLabel(g),
          WORLDS[g.settings.style]?.name ?? 'Kopce',
          g.settings.mode === 'deathmatch' ? null : `${formatLimit(g.settings.turnLimitSec)} na tah`,
          `${g.players.length}/${g.settings.maxPlayers}`,
        ]
          .filter(Boolean)
          .join(' · '),
      ),
      h('span', { class: 'muted small' }, right),
    );
  }

  function renderList() {
    if (!games.length) {
      list.replaceChildren(h('p', { class: 'muted', style: { margin: '0' } }, 'Zatím žádné hry. Založ novou a pošli kamarádům kód.'));
      return;
    }
    const sorted = games.slice().sort((a, b) => rank(a) - rank(b) || b.updatedAt - a.updatedAt);
    const active = sorted.filter((g) => g.status !== 'finished');
    const finished = sorted.filter((g) => g.status === 'finished');
    finishedSummary.textContent = `Dohrané hry (${finished.length})`;
    finishedList.replaceChildren(...finished.map(gameItem));
    list.replaceChildren(
      ...(active.length
        ? active.map(gameItem)
        : [h('p', { class: 'muted', style: { margin: '0' } }, 'Žádná rozehraná hra.')]),
      ...(finished.length ? [finishedBox] : []),
    );
  }

  async function load() {
    try {
      games = (await api.games()).games;
      renderList();
    } catch (e) {
      toast((e as Error).message, true);
    }
  }

  // --- Nová hra ---
  const limit = h(
    'select',
    { name: 'limit' },
    ...TURN_LIMITS.map((s) => h('option', { value: String(s), selected: s === 24 * 3600 }, formatLimit(s))),
  );
  const maxPlayers = h(
    'select',
    { name: 'max' },
    ...[2, 3, 4, 5, 6, 7, 8].map((n) => h('option', { value: String(n), selected: n === 4 }, String(n))),
  );
  const style = h(
    'div',
    { class: 'seg' },
    ...TERRAIN_STYLES.map((st) =>
      h('label', {}, h('input', { type: 'radio', name: 'style', value: st, checked: st === 'hills' }), h('span', {}, `${WORLDS[st].icon} ${WORLDS[st].name}`)),
    ),
  );
  const rounds = h(
    'select',
    { name: 'rounds' },
    ...TOURNAMENT_ROUNDS.map((n) => h('option', { value: String(n), selected: n === 3 }, roundsText(n))),
  );
  const roundsRow = h('label', { hidden: true }, 'Počet kol', rounds);
  const modeHint = h('p', { class: 'muted small', style: { margin: '0' } });
  const limitRow = h('label', {}, 'Čas na tah', limit);
  const modeOf = () => (mode.querySelector('input:checked') as HTMLInputElement).value;
  const syncMode = () => {
    const m = modeOf();
    roundsRow.hidden = m !== 'tournament';
    limitRow.hidden = m === 'deathmatch';
    modeHint.textContent =
      m === 'tournament'
        ? 'Stejní hráči odehrají několik kol na nových mapách. Za umístění v kole jsou body (poslední 0, každé lepší místo +1), vyhrává nejvíc bodů.'
        : m === 'deathmatch'
          ? 'Bez tahů – všichni online střílí najednou. Po výstřelu se 3 s nabíjí, palivo se průběžně dobíjí, munice je omezená. Zničený tank je vyřazený; konec, když zbude jeden tank nebo všem dojde munice (vyhrává nejvíc zabití).'
          : 'Kdo nestihne odehrát tah v limitu, je automaticky vyřazen.';
  };
  const mode = h(
    'div',
    { class: 'seg', onchange: syncMode },
    h('label', {}, h('input', { type: 'radio', name: 'mode', value: 'classic', checked: true }), h('span', {}, '⚔ Klasika')),
    h('label', {}, h('input', { type: 'radio', name: 'mode', value: 'tournament' }), h('span', {}, '🏆 Turnaj')),
    h('label', {}, h('input', { type: 'radio', name: 'mode', value: 'deathmatch' }), h('span', {}, '💥 Deathmatch')),
  );
  syncMode();
  const createBtn = h('button', { class: 'primary', type: 'submit' }, 'Založit hru');
  const createForm = h(
    'form',
    {
      class: 'card',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        createBtn.disabled = true;
        try {
          const styleVal = (style.querySelector('input:checked') as HTMLInputElement).value;
          const { game } = await api.createGame({
            turnLimitSec: Number(limit.value),
            maxPlayers: Number(maxPlayers.value),
            style: styleVal,
            mode: modeOf(),
            rounds: Number(rounds.value),
          });
          go(`/game/${game.id}`);
        } catch (err) {
          toast((err as Error).message, true);
        } finally {
          createBtn.disabled = false;
        }
      },
    },
    h('h3', {}, 'Nová hra'),
    mode,
    h('div', { class: 'grid2' }, limitRow, h('label', {}, 'Max. hráčů', maxPlayers)),
    roundsRow,
    style,
    modeHint,
    createBtn,
  );

  // --- Připojit ---
  const code = h('input', {
    placeholder: 'KÓD',
    maxlength: 6,
    autocapitalize: 'characters',
    autocomplete: 'off',
    style: { textTransform: 'uppercase', letterSpacing: '0.15em' },
  });
  const joinForm = h(
    'form',
    {
      class: 'card',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        if (!code.value.trim()) return;
        try {
          const { game } = await api.join(code.value);
          go(`/game/${game.id}`);
        } catch (err) {
          toast((err as Error).message, true);
        }
      },
    },
    h('h3', {}, 'Připojit se ke hře'),
    h('div', { class: 'row' }, h('div', { class: 'grow' }, code), h('button', { type: 'submit' }, 'Připojit')),
  );

  const logout = h(
    'button',
    {
      class: 'ghost',
      onclick: async () => {
        await api.logout().catch(() => {});
        session.me = null;
        location.hash = '#/login';
      },
    },
    'Odhlásit',
  );

  const cleanups: (() => void)[] = [];
  const editor = tankEditor();
  const board = leaderboardCard();
  const tankCard = h(
    'details',
    { class: 'card' },
    h('summary', {}, h('h3', { style: { display: 'inline' } }, '🛠 Můj tank')),
    editor.el,
  );

  const el = h(
    'div',
    { class: 'backdrop' },
    h(
      'div',
      { class: 'page' },
      h(
        'div',
        { class: 'topbar' },
        h('div', { class: 'logo' }, 'TANKY'),
        h(
          'div',
          { class: 'row' },
          h('a', { class: 'muted', href: `#/player/${session.me!.id}`, title: 'Můj profil a statistiky' }, `📊 ${session.me!.name}`),
          notificationButton('ghost', cleanups),
          soundButton('ghost', cleanups),
          h('button', { class: 'ghost', type: 'button', title: 'Nastavení zvuku', onclick: () => go('/sounds') }, '🎧 Zvuk'),
          logout,
        ),
      ),
      h(
        'button',
        { class: 'train-entry', onclick: () => go('/training') },
        h('span', { class: 'ico' }, '🎯'),
        h('span', {}, h('b', {}, 'Trénink'), h('small', {}, 'Nastav si vítr, rozmísti cvičné terče a střílej s neomezenou municí.')),
      ),
      h('div', { class: 'card' }, h('h3', {}, 'Moje hry'), list),
      board.el,
      createForm,
      joinForm,
      tankCard,
    ),
  );

  const off = socket.on((m) => {
    if (m.t === 'games_changed') {
      load();
      board.reload();
    }
  });
  const offOpen = socket.onOpen(load);
  const tick = window.setInterval(renderList, 15000);
  socket.setGame(null);
  load();

  return {
    el,
    destroy() {
      off();
      offOpen();
      clearInterval(tick);
      editor.destroy();
      for (const c of cleanups) c();
    },
  };
}
